import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getWeekdayNightSlots, type DateSlot } from "@/lib/dates";
import type { UserResponse } from "@/hooks/useAvailabilityData";

interface AvailabilityGridProps {
  userResponse?: UserResponse | null;
  availabilityCounts?: Record<string, number>;
  onSave: (availability: string[], name: string) => Promise<void>;
}


export function AvailabilityGrid({
  userResponse,
  availabilityCounts,
  onSave,
}: AvailabilityGridProps) {
  const slots = getWeekdayNightSlots();
  const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (userResponse?.availability) {
      setSelectedSlots(new Set(userResponse.availability));
    } else {
      // Clear selections when userResponse is null or has no availability
      setSelectedSlots(new Set());
    }
    if (userResponse?.name) {
      setName(userResponse.name);
    } else {
      // Clear name when userResponse is null
      setName("");
    }
  }, [userResponse]);

  const toggleSlot = (slotId: string) => {
    const newSelected = new Set(selectedSlots);
    if (newSelected.has(slotId)) {
      newSelected.delete(slotId);
    } else {
      newSelected.add(slotId);
    }
    setSelectedSlots(newSelected);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      return;
    }
    setSaving(true);
    try {
      await onSave(Array.from(selectedSlots), name.trim());
    } finally {
      setSaving(false);
    }
  };

  // Check if user has saved availability
  const hasSavedAvailability = userResponse?.availability && userResponse.availability.length > 0;

  // Check if current selection matches saved availability
  const savedSlots = hasSavedAvailability ? new Set(userResponse.availability as string[]) : new Set<string>();
  const hasChanges = selectedSlots.size !== savedSlots.size ||
    Array.from(selectedSlots).some(slot => !savedSlots.has(slot)) ||
    Array.from(savedSlots).some(slot => !selectedSlots.has(slot));

  // Determine button text
  const buttonText = hasSavedAvailability
    ? `Update Availability (${selectedSlots.size} selected)`
    : `Save Availability (${selectedSlots.size} selected)`;

  // Group slots by week (each Monday starts a new week)
  const weeks: DateSlot[][] = [];
  let currentWeek: DateSlot[] = [];

  slots.forEach((slot) => {
    if (slot.dayName === "Monday" && currentWeek.length > 0) {
      weeks.push(currentWeek);
      currentWeek = [slot];
    } else {
      currentWeek.push(slot);
    }
  });
  if (currentWeek.length > 0) {
    weeks.push(currentWeek);
  }

  return (
    <div className="planner-box">
      <h2 className="planner-title">📅 Select Your Availability</h2>
      <p className="planner-text">
        Click on the dates below to select your availability.
      </p>

      <div className="availability-weeks">
        {weeks.map((weekSlots, weekIndex) => (
          <div key={weekIndex} className="availability-week">
            {weekSlots.map((slot) => {
              const isSelected = selectedSlots.has(slot.id);
              const count = availabilityCounts?.[slot.id] || 0;

              return (
                <button
                  key={slot.id}
                  type="button"
                  onClick={() => toggleSlot(slot.id)}
                  className={`availability-slot ${isSelected ? "selected" : ""}`}
                >
                  <div className="slot-day">{slot.dayName}</div>
                  <div className="slot-date">{slot.displayDate}</div>
                  <div className="slot-count">
                    {count} votes
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="form-group" style={{ marginTop: "1.5rem" }}>
        <Label htmlFor="name-availability" className="planner-label">
          Your Name: <span className="required">*</span>
        </Label>
        <Input
          id="name-availability"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="planner-input"
          placeholder="Your full name"
        />
      </div>

      <div className="availability-actions">
        <Button
          onClick={handleSave}
          disabled={saving || selectedSlots.size === 0 || !name.trim() || (hasSavedAvailability && !hasChanges)}
          className="planner-button"
        >
          {saving ? "Saving..." : buttonText}
        </Button>
      </div>
    </div>
  );
}

